import { useEffect, useState } from 'react';

interface CategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCategory: (category: string) => void;
  categories: string[];
  platform: 'ios' | 'android';
}

function IOSModal({ isOpen, onClose, onSelectCategory, categories }: Omit<CategoriesModalProps, 'platform'>) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationState, setAnimationState] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setTimeout(() => {
        setAnimationState(true);
      }, 10);
    } else {
      setAnimationState(false);
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      {/* Semi-transparent Overlay */}
      <div
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ${
          animationState ? 'opacity-20' : 'opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`fixed bottom-0 inset-x-0 z-50 bg-white rounded-t-3xl max-h-[80vh] overflow-y-auto shadow-lg transform transition-transform duration-300 ease-out ${
          animationState ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {/* Handle/Indicator */}
        <div className="flex justify-center pt-3 pb-4">
          <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
        </div>

        {/* Title */}
        <div className="px-6 pb-4">
          <h2 className="label-large">More categories</h2>
        </div>

        {/* Categories List */}
        <div className="px-6 pb-12">
          {categories.map((category) => (
            <div
              key={category}
              className="py-4"
              onClick={() => {
                onSelectCategory(category);
                onClose();
              }}
            >
              <span className="paragraph-medium">{category}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function AndroidModal({ isOpen, onClose, onSelectCategory, categories }: Omit<CategoriesModalProps, 'platform'>) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white min-h-screen flex flex-col overflow-y-auto">
      {/* Header with back button */}
      <header className="sticky top-0 left-0 right-0 z-20 bg-white px-4 py-4 flex items-center">
        <button className="p-1" onClick={onClose} aria-label="Back">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="w-6"></div> {/* Spacer for alignment */}
      </header>

      {/* Content */}
      <div className="px-6 flex-1">
        {categories.map((category) => (
          <div
            key={category}
            className="py-4 active:bg-gray-100"
            onClick={() => {
              onSelectCategory(category);
              onClose();
            }}
          >
            <span className="paragraph-medium">{category}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CategoriesModal(props: CategoriesModalProps) {
  const { platform, ...modalProps } = props;

  return platform === 'ios'
    ? <IOSModal {...modalProps} />
    : <AndroidModal {...modalProps} />;
}